<template>
  <div class="flex-between mb-[6px] min-h-[32px]">
    <div class="flex gap-x-[10px]">
      <CheckAll
        v-if="state.batchVisible"
        :list="list"
        :checkList="state.checkList"
        @check-all-change="checkListChange"
      />
      <span class="text-font-60 text-14">{{
        $t('gong-total-ge', [total])
      }}</span>
    </div>
    <div class="flex-center gap-x-[12px]">
      <slot name="extra" />
      <ButtonList
        :list="batchHands"
        :data="list"
        :checkList="state.checkList"
        :keyItem="keyItem"
        :reload="() => updateData('reload')"
      />
    </div>
  </div>
  <div v-if="loading" class="loading">
    <a-spin />
  </div>
  <EmptyView v-if="dataSource.length === 0 && !first">
    <template v-if="$slots?.emptyContent" #content>
      <slot name="emptyContent" />
    </template>
    <template v-if="$slots?.emptyExtra && !!upload" #extra>
      <slot
        name="emptyExtra"
        :uploadOpen="uploadOpen"
        :updateData="updateData"
        :upload="
          upload ? { accept: upload.accept, params: dataType } : undefined
        "
      />
    </template>
  </EmptyView>
  <div
    v-else-if="!first"
    ref="scrollRef"
    class="card-scroll-bar pb-[50px]"
    @scroll="onScroll"
  >
    <a-checkbox-group
      v-model:value="state.checkList"
      class="brand-ground grid gap-[24px_20px] !cursor-default min-h-[156px]"
      :class="{
        'grid-cols-5 md:grid-cols-4': !cols || cols === 5,
        'grid-cols-6 md:grid-cols-5': cols === 6,
      }"
    >
      <Upload
        v-if="!!upload"
        ref="uploadRef"
        :title="$t('shang-chuan') + (title || $t('wen-jian'))"
        :loca="!!upload.loca"
        :open="uploadOpen"
        :close="handClose"
        :timer="initTimer"
        :dataType="dataType"
        :rights="rights"
      >
        <template #frag>
          <slot
            name="frag"
            :updateData="updateData"
            :upload="{ accept: upload.accept, params: dataType }"
          />
        </template>
      </Upload>
      <template
        v-for="(x, index) in dataSource"
        :key="x.id || x.upload_secret_id"
      >
        <UploadItem
          v-if="x.upload_secret_id || x.uid"
          :data="x"
          :rights="rights"
          :dataType="dataType"
          :close="handClose"
          :cancel="handCancel"
        />
        <slot
          v-else
          name="item"
          :record="x"
          :index="index"
          :check="state.batchVisible"
          :updateData="updateData"
        />
      </template>
    </a-checkbox-group>
    <Divider v-if="list.length === total && total >= 15" />
  </div>
</template>

<script lang="ts" setup>
import { CheckAll } from './checkAll'
import { ButtonList, type ButtonItem } from './buttonList'
import Upload from './upload.vue'
import UploadItem from './uploadItem.vue'
import { type DataType } from './card'
import { useUploadList } from '~/hooks/useUploadList'
import { usePage, type PageOptions } from '~/hooks/usePage'
import { useBatch } from '~/hooks/useBatch'
import { reqRightsDisplay } from '~/api'
import { USER_LEVEL_KEY } from '~/api/interface'

interface IProps {
  title?: string
  options: PageOptions
  cols?: number
  upload?: {
    accept?: string
    loca?: boolean
    onSuccess?: (file: File) => void
  }
  dataType?: DataType
  keyItem?: any
  batchs?: ButtonItem[]
  editData?: {
    [key: string]: (data: any, value: any, source?: any) => Promise<any>
  }
}

const props = defineProps<IProps>()
const uploadRef = ref()
const scrollRef = ref()
const upType = ref()
const { $t } = useNuxtApp()
const shareVisible = useShare()

const { loading, list, next, reload, total, first } = usePage(props.options)
const { state, exitBatch, Bulk, Exit } = useBatch()
const { handOpen, fileList, initTimer, closeTimer, handClose, handCancel } =
  useUploadList({
    title: props?.title,
    accept: props.upload?.accept,
    params: props.dataType,
    onSuccess: (file: File) => {
      props.upload?.onSuccess?.(file)
      reload()
    },
  })

const dataSource = computed(() => {
  return [...fileList.value, ...list.value].sort((a, b) => {
    return (
      new Date(b.brand_time || b.create_time).getTime() -
      new Date(a.brand_time || a.create_time).getTime()
    )
  })
})

useBackToTop(() => ({
  scrollContainer: '.card-scroll-bar',
  item: '.card',
  total: total.value + fileList.value.length,
}))

const uploadOpen = () => {
  handOpen?.({ accept: props.upload?.accept })
}

const batchHands = computed(() => {
  const list =
    props?.batchs?.filter((item) => {
      if (!item) {
        return false
      }

      return !item.key || checkPermission(item.key)
    }) || []
  if (list.length === 0) {
    return []
  }

  return !state.batchVisible ? [Bulk] : [...list, Exit]
})

const checkListChange = (v: boolean) => {
  state.checkList = v ? list.value.map((item) => item.id) : []
}

const updateData = async (
  key: string,
  value?: any,
  data?: Record<string, any>,
  source?: any
) => {
  if (key === 'reload') {
    state.checkList = []
    reload()
    return
  }
  const update = () => {
    const item = list.value.find((item) => item.id === data?.id)
    item[key] = value
  }
  if (key === 'edit') {
    if (source !== undefined) {
      upType.value = source
    }

    update()
    return
  }
  if (props.editData && props.editData[key] && value && value !== data?.[key]) {
    try {
      const res = await props.editData?.[key]?.(data, value, upType.value)
      if (res.code !== 0) {
        return
      }

      message.success($t('xiu-gai-cheng-gong'))
      update()
    } catch (error) {
      console.error(error)

      updateData('edit', true, data, source)
    }
  }
}

const onScroll = (e: Event) => {
  const { scrollTop, scrollHeight, clientHeight } = e.target as HTMLBRElement
  if (Math.round(scrollTop + clientHeight) >= scrollHeight - 1) {
    next()
  }

  if (!shareVisible.value) {
    return
  }

  shareVisible.value = false
}

const toScrollTop = async () => {
  await nextTick()
  scrollRef.value?.scrollTo(0, 0)
}

// const userRights = getCookie(COOKIE_KEY.USER_RIGHTS) as any
const userInfo = useUserInfo().value
const rights = ref<Record<string, any>[]>([])

const getRights = async () => {
  const res = await reqRightsDisplay()
  if (res.code !== 0) {
    return
  }

  if (!userInfo) {
    return
  }

  rights.value =
    res?.data?.rights.filter((x) => {
      const isOrg = [
        USER_LEVEL_KEY.ORG_STANDARD,
        USER_LEVEL_KEY.ORG_FLAGSHIP,
      ].includes(userInfo.rights_type)

      return x.rights_type > (isOrg ? 24 : userInfo.rights_type)
    }) || []
}

getRights()

reload()

onMounted(() => {
  initTimer()
})

onBeforeUnmount(() => {
  closeTimer()
})

defineExpose({
  reload,
  exitBatch,
  updateData,
  toScrollTop,
})
</script>

<style lang="scss" scoped>
.loading {
  @apply absolute top-0 left-0 w-full h-full flex-center bg-back-bg bg-opacity-50 z-10;
}
.card-scroll-bar {
  @apply relative overflow-y-auto pr-[10px] pt-[18px];
}
</style>
